import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { clientApi, WorkspaceDiscovery, WorkspaceDraft, WorkspaceUniversePolicy } from '@/api/client'
import ErrorBox from '@/components/ErrorBox'
import Topbar from '@/components/Topbar'

const EXAMPLES = [
  'Assuming AI continues to improve and scale up to more of the economy, who are the non-direct/non recognised beneficiaries in terms of company growth and value?',
  "Are the AI companies on the HK stock exchange like DeepSeek and Moonshot, Z.ai, etc a better investment than waiting to invest in OpenAI or Anthropic's Retail IPO?",
  'What would need to happen for our GLP-1 exposure to become a bear case?',
  'Which defense contractors are most exposed to a shift in NATO procurement?',
  'Should we worry about regulatory risk in European payments infrastructure?',
  'How should a regional bank adjust deposit pricing, loan growth, liquidity and duration risk if rates stay higher for longer over the next 12 months?',
  'What macro conditions would change our commercial real estate underwriting assumptions over the next year?',
  'What are the key forecastable risks to this portfolio over the next 6 months?',
]

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="workspace-section">
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800 }}>{title}</div>
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  )
}

function discoveryPlaceholder(questionId?: string | null) {
  switch (String(questionId || '')) {
    case 'decision_use':
      return 'Example: Set rollout gates for AI coding agents, or rank public companies to diligence first.'
    case 'scope':
    case 'universe':
      return 'Example: Include public security advisories and vendor disclosures; exclude internal SOC alerts and private ticket queues.'
    case 'investment_route_scope':
      return 'Example: compare HK-listed proxies against OpenAI/Anthropic IPO timing.'
    case 'portfolio_scope':
      return 'Example: NVDA, TSM, AVGO, AMD, ANET, MRVL, MSFT, AMZN, GOOGL, META.'
    case 'ranking_basis':
      return 'Example: Rank by severity first, then how early the signal appears and how externally verifiable it is.'
    case 'horizon':
      return 'Example: 12 months.'
    default:
      return 'Type a short response or pick the closest option.'
  }
}

function DraftPreview({ draft, workspaceId, onOpen }: { draft: WorkspaceDraft; workspaceId: string; onOpen: () => void }) {
  const suppliedEntities = draft.universe_policy?.supplied_entities || []
  const referenceEntities = suppliedEntities.filter((entity) => ['reference_only', 'excluded'].includes(String(entity.candidate_disposition || '')))
  return (
    <div className="workspace-draft">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="muted" style={{ fontSize: 12 }}>Proposed living dashboard</div>
          <h2 style={{ margin: '4px 0 0', fontSize: 22, lineHeight: 1.15 }}>{draft.title}</h2>
          <p className="muted" style={{ margin: '8px 0 0', maxWidth: 760 }}>Cenva will turn the question into a monitored read: signal checks, evidence receipts, weakening conditions, and refresh targets.</p>
        </div>
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <span className="pill ok">{draft.horizon}</span>
        </div>
      </div>

      {draft.missing_inputs?.length > 0 && (
        <div className="card" style={{ marginTop: 14, borderColor: 'rgba(251,191,36,0.22)', background: 'rgba(251,191,36,0.06)' }}>
          <div style={{ fontWeight: 750 }}>Optional refinements</div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {draft.missing_inputs.map((x, i) => <li key={i} className="muted" style={{ marginTop: 4 }}>{x}</li>)}
          </ul>
        </div>
      )}

      <div className="workspace-draft-grid">
        <Section title="Assumptions">
          <div className="row" style={{ gap: 6 }}>
            <span className="pill">{draft.workspace_type.replaceAll('_', ' ')}</span>
            {draft.assumptions?.slice(0, 3).map((x, i) => <span key={i} className="pill workspace-wrap-pill">{x}</span>)}
          </div>
        </Section>

        {referenceEntities.length ? (
          <Section title="Reference portfolio">
            <div className="row" style={{ gap: 6 }}>
              {referenceEntities.slice(0, 20).map((entity, i) => (
                <span key={i} className="pill">{entity.ticker || entity.name}{entity.ticker && entity.name ? ` · ${entity.name}` : ''}</span>
              ))}
            </div>
            <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>These companies provide portfolio context and cannot enter the discovered ranking.</div>
          </Section>
        ) : null}

        <Section title="Candidate universe">
          {draft.entities?.length ? (
            <div className="row" style={{ gap: 6 }}>
              {draft.entities.slice(0, 12).map((e, i) => (
                <span key={i} className="pill">{e.ticker || e.name}{e.ticker && e.name ? ` · ${e.name}` : ''}</span>
              ))}
            </div>
          ) : <div className="muted">No candidates are pinned. Cenva will discover companies from the question and candidate scope.</div>}
        </Section>

        <Section title="Drivers">
          <div className="workspace-driver-list">
            {draft.drivers?.map((d, i) => (
              <div key={i} className="workspace-driver">
                <div style={{ fontWeight: 750 }}>{d.name}</div>
                <div className="muted" style={{ marginTop: 3, lineHeight: 1.45 }}>{d.description}</div>
              </div>
            ))}
          </div>
        </Section>

        {draft.scoping_artifacts?.length ? (
        <Section title="Evidence Prep">
            <div className="workspace-driver-list">
              {draft.scoping_artifacts.map((a: any, i: number) => (
                <div key={i} className="workspace-driver">
                  <div style={{ fontWeight: 750 }}>{String(a.type || 'scoping').replaceAll('_', ' ')}</div>
                  <div className="muted" style={{ marginTop: 3, lineHeight: 1.45 }}>{a.description}</div>
                  {a.output && <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>Output: {a.output}</div>}
                </div>
              ))}
            </div>
          </Section>
        ) : null}

        <Section title="Signal Checks">
          <div className="workspace-question-list">
            {draft.forecast_questions?.map((q, i) => (
              <div key={i} className="workspace-question-row">
                <div className="pill">{i + 1}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{q.title}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{q.driver} · {q.why_it_matters}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Monitoring Targets">
          <div className="workspace-driver-list">
            {draft.watchpoints?.map((w, i) => (
              <div key={i} className="workspace-driver">
                <div style={{ fontWeight: 750 }}>{w.title}</div>
                <div className="muted" style={{ marginTop: 3 }}>{w.why_it_matters}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="How Cenva Will Build the Read">
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {draft.report_outline?.map((x, i) => <li key={i} style={{ marginTop: 4 }}>{x}</li>)}
          </ol>
        </Section>
      </div>
    </div>
  )
}

function DiscoveryPanel({
  workspaceId,
  discovery,
  answering,
  planning,
  refining,
  answerValue,
  refinementValue,
  setAnswerValue,
  setRefinementValue,
  onAnswer,
  onPlan,
  onRefine,
  onOpen,
  onAuto,
  hasDraft,
}: {
  workspaceId: string
  discovery: WorkspaceDiscovery
  answering: boolean
  planning: boolean
  refining: boolean
  answerValue: string
  refinementValue: string
  setAnswerValue: (value: string) => void
  setRefinementValue: (value: string) => void
  onAnswer: () => void
  onPlan: () => void
  onRefine: () => void
  onOpen: () => void
  onAuto: () => void
  hasDraft: boolean
}) {
  const q = discovery.next_question
  const options = Array.isArray(q?.options) ? q.options : []
  const answers = discovery.answers || {}
  const answeredRows = (discovery.clarifying_questions || []).filter((row: any) => answers?.[String(row?.id)] !== undefined && answers?.[String(row?.id)] !== '')
  const known = Array.isArray(discovery.guided_intake?.known_so_far) ? discovery.guided_intake.known_so_far : []
  const entityRows = Array.isArray(discovery.guided_intake?.entity_status?.entities) ? discovery.guided_intake.entity_status.entities : []
  return (
    <div className="workspace-chat-card">
      <div className="workspace-chat-header">
        <div>
          <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>{hasDraft ? 'Workspace proposal' : 'Scope check'}</div>
          <h2>{hasDraft ? 'Review the dashboard Cenva will build.' : (discovery.archetype?.label || 'Forecast workspace')}</h2>
        </div>
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
          <span className="pill ok">{discovery.horizon || 'Horizon TBD'}</span>
          <span className="pill">{String(discovery.stage || '').replaceAll('_', ' ')}</span>
        </div>
      </div>

      <div className="workspace-chat-thread">
        <div className="workspace-chat-bubble user">
          <small>Your question</small>
          <div>{discovery.original_question}</div>
        </div>
        <div className="workspace-chat-bubble assistant">
          <small>Cenva</small>
          <div>{discovery.guided_intake?.assistant_message || discovery.archetype?.description || 'I can propose a workspace from this question and ask only if something blocks the build.'}</div>
          {known.length ? <div className="workspace-chat-pills">{known.slice(0, 6).map((x: any, i: number) => <span key={i} className="pill">{String(x)}</span>)}</div> : null}
          {entityRows.length ? (
            <div className="workspace-entity-status">
              {entityRows.slice(0, 5).map((row: any, i: number) => (
                <span key={`${row?.name || i}`} className="pill">{row?.name}: {String(row?.listing_status || 'needs verification').replaceAll('_', ' ')}</span>
              ))}
            </div>
          ) : null}
        </div>
        {answeredRows.map((row: any) => (
          <div key={String(row.id)} className="workspace-chat-bubble user compact">
            <small>{row.question}</small>
            <div>{String(answers[String(row.id)])}</div>
          </div>
        ))}
        {q ? (
          <div className="workspace-chat-bubble assistant active">
            <small>One clarification before building</small>
            <div>{q.question}</div>
            {q.help_text ? <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{q.help_text}</div> : null}
            {options.length > 0 ? (
              <div className="workspace-chat-options">
                {options.map((opt: any) => (
                  <button key={String(opt)} className={answerValue === String(opt) ? 'btn primary' : 'btn'} onClick={() => setAnswerValue(String(opt))}>
                    {String(opt)}
                  </button>
                ))}
              </div>
            ) : (
              <input className="input" value={answerValue} onChange={(e) => setAnswerValue(e.target.value)} placeholder={discoveryPlaceholder(q?.id)} />
            )}
          </div>
        ) : (
          <div className="workspace-chat-bubble assistant active">
            <small>{hasDraft ? 'Proposal ready' : 'Ready'}</small>
            <div>{hasDraft ? 'The proposed dashboard is below. Build it as-is to scope the companies and run the forecasts, or add constraints and regenerate the proposal first.' : 'Cenva has enough to create the workspace proposal. You can add constraints later from the workspace detail page.'}</div>
            {hasDraft ? (
              <textarea
                className="input workspace-context-input"
                value={refinementValue}
                onChange={(e) => setRefinementValue(e.target.value)}
                placeholder="Optional: add constraints, exclusions, source preferences, entities, or a sharper decision lens before building."
                style={{ marginTop: 10 }}
              />
            ) : null}
          </div>
        )}
      </div>

      <div className="workspace-chat-actions">
        {q ? (
          <button className="btn primary" onClick={onAnswer} disabled={answering || !answerValue.trim()}>
            {answering ? 'Saving...' : 'Save and continue'}
          </button>
        ) : null}
        {hasDraft ? (
          <button className="btn primary" onClick={onOpen} title="Scope the companies and approve the shortlist yourself, then Cenva runs the forecasts.">Review companies first →</button>
        ) : q ? null : (
          <button className={q ? 'btn' : 'btn primary'} onClick={onPlan} disabled={planning}>
            {planning ? 'Creating proposal...' : q ? 'Create proposal with current response' : 'Create workspace proposal'}
          </button>
        )}
        {hasDraft ? (
          <button className="btn" onClick={onAuto} title="Cenva picks the shortlist and runs the forecasts automatically — no review step.">Decide for me ✨</button>
        ) : null}
        {hasDraft && !q ? (
          <button className="btn" onClick={onRefine} disabled={refining || planning || !refinementValue.trim()}>
            {refining ? 'Regenerating...' : 'Regenerate proposal'}
          </button>
        ) : null}
        <button className="btn" onClick={() => window.location.assign(`/workspaces/${workspaceId}`)}>Open without building</button>
        <span className="muted workspace-chat-saved">{hasDraft ? 'Proposal saved' : 'Workspace saved'}</span>
      </div>
      {hasDraft ? (
        <p className="muted" style={{ fontSize: 12, marginTop: 8, maxWidth: 760 }}>
          <strong>Two ways to build.</strong> <strong>Review companies first</strong> scopes the candidate companies (~2–5 min) and lets you approve the shortlist before Cenva runs the forecasts — you stay in control of the universe. <strong>Decide for me</strong> hands the whole thing to Cenva: it picks the shortlist and runs the forecasts automatically. Either way it runs in the background and you can leave the page.
        </p>
      ) : null}
    </div>
  )
}

export default function WorkspaceNew() {
  const nav = useNavigate()
  // Seed from a ?q= handoff (the ask-box on the Workspaces home), else an example.
  const [searchParams] = useSearchParams()
  const seededQuestion = (searchParams.get('q') || '').trim()
  const [question, setQuestion] = useState(seededQuestion || EXAMPLES[0])
  const [context, setContext] = useState('')
  const [portfolio, setPortfolio] = useState('')
  const [portfolioUse, setPortfolioUse] = useState<'supplied_only' | 'supplied_and_discovered' | 'discovered_only'>('supplied_only')
  const [workspaceId, setWorkspaceId] = useState('')
  const [discovery, setDiscovery] = useState<WorkspaceDiscovery | null>(null)
  const [draft, setDraft] = useState<WorkspaceDraft | null>(null)
  const [loading, setLoading] = useState(false)
  const [answering, setAnswering] = useState(false)
  const [planning, setPlanning] = useState(false)
  const [refining, setRefining] = useState(false)
  const [answerValue, setAnswerValue] = useState('')
  const [refinementValue, setRefinementValue] = useState('')
  const [error, setError] = useState<any>(null)

  async function handleStartDiscovery() {
    setError(null)
    setLoading(true)
    setDraft(null)
    setRefinementValue('')
    try {
      const portfolioText = portfolio.trim().replace(/\r?\n/g, '; ')
      const compiledContext = [context.trim(), portfolioText ? `portfolio_holdings: ${portfolioText}` : ''].filter(Boolean).join('\n')
      const universePolicy: WorkspaceUniversePolicy | undefined = portfolioText ? {
        candidate_source: portfolioUse,
        candidate_scope: portfolioUse === 'discovered_only' ? 'Publicly listed companies outside the supplied reference portfolio' : undefined,
        shortlist_limit: 10,
        supplied_entities: [],
      } : undefined
      const res = await clientApi.startWorkspaceDiscovery({
        question,
        context: compiledContext || null,
        max_questions: 12,
        universe_policy: universePolicy,
      })
      setWorkspaceId(res.workspace_id)
      setDiscovery(res.discovery)
      setDraft(res.draft || null)
      const opts = Array.isArray(res.discovery.next_question?.options) ? res.discovery.next_question.options : []
      setAnswerValue(opts.length ? String(opts[0]) : '')
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  async function handleAnswerDiscovery() {
    const qid = discovery?.next_question?.id
    if (!workspaceId || !qid) return
    setError(null)
    setAnswering(true)
    try {
      const res = await clientApi.answerWorkspaceDiscovery(workspaceId, { answers: { [String(qid)]: answerValue } })
      setDiscovery(res.discovery)
      const opts = Array.isArray(res.discovery.next_question?.options) ? res.discovery.next_question.options : []
      setAnswerValue(opts.length ? String(opts[0]) : '')
    } catch (e) {
      setError(e)
    } finally {
      setAnswering(false)
    }
  }

  async function handlePlanDiscovery() {
    if (!workspaceId) return
    setError(null)
    setPlanning(true)
    try {
      const res = await clientApi.planWorkspaceDiscovery(workspaceId)
      setDiscovery(res.discovery)
      setDraft(res.draft)
    } catch (e) {
      setError(e)
    } finally {
      setPlanning(false)
    }
  }

  async function openAndStartScoping() {
    // "Build this workspace" starts the first real build stage (async
    // layer-0 scoping) before navigating, so the user lands on a page that
    // is visibly working — not a draft silently waiting for more clicks.
    // AWAIT the scope call: the endpoint writes the `queued` scoping marker
    // synchronously, and the detail page only starts polling once it sees
    // that marker. Firing-and-forgetting raced the navigation — the detail
    // page could load before the marker existed, so it never polled and sat
    // on "Waiting for shortlist..." until a manual reload.
    if (!workspaceId) return
    setError(null)
    try {
      await clientApi.scopeWorkspace(workspaceId)
    } catch (e) {
      // Previously this swallowed the failure and navigated anyway, so a
      // rejected build landed the client on a detail page that silently never
      // progressed. Surface it here instead — the workspace is still saved and
      // Build can be pressed again.
      setError(e)
      return
    }
    nav(`/workspaces/${workspaceId}`)
  }

  async function buildEverything() {
    // "I'm feeling lucky" — start scoping, then hand off with ?auto=1 so the
    // detail page auto-approves the shortlist and runs the forecasts without
    // the manual review step. For users who just want the finished read.
    if (!workspaceId) return
    setError(null)
    try {
      await clientApi.scopeWorkspace(workspaceId)
    } catch (e) {
      // Same reasoning as openAndStartScoping: never hand the client a page
      // that looks like it's working when the build was actually refused.
      setError(e)
      return
    }
    nav(`/workspaces/${workspaceId}?auto=1`)
  }

  async function handleRefineProposal() {
    const refinement = refinementValue.trim()
    if (!workspaceId || !discovery || !refinement) return
    setError(null)
    setRefining(true)
    try {
      const existing = String(discovery.answers?.proposal_refinement || '').trim()
      const combined = existing ? `${existing}\n${refinement}` : refinement
      const answered = await clientApi.answerWorkspaceDiscovery(workspaceId, { answers: { proposal_refinement: combined } })
      setDiscovery(answered.discovery)
      const planned = await clientApi.planWorkspaceDiscovery(workspaceId)
      setDiscovery(planned.discovery)
      setDraft(planned.draft)
      setRefinementValue('')
    } catch (e) {
      setError(e)
    } finally {
      setRefining(false)
    }
  }

  return (
    <div className="container">
      <Topbar subtitle="New Forecast Workspace" />
      <div className={discovery ? 'workspace-hero compact' : 'workspace-hero'}>
        <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>Blank sheet</div>
        <h1>{discovery ? 'Review the workspace Cenva proposes.' : 'Describe the dashboard you want Cenva to maintain.'}</h1>
        <p className="muted">{discovery ? 'Build the proposal as-is or respond to the one visible blocker if Cenva genuinely needs scope.' : 'Ask a strategic question. Cenva proposes the monitored read, signal checks, evidence receipts, and refresh targets.'}</p>

        {!discovery && (
          <>
            <textarea
              className="input workspace-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What client question should Cenva turn into a forecast workspace?"
            />
            <textarea
              className="input workspace-context-input"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Optional: add the research thesis, eligible market, source notes, or other constraints."
            />
            <div className="workspace-section" style={{ marginTop: 12 }}>
              <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800 }}>Portfolio (optional)</div>
              <textarea
                className="input workspace-context-input"
                value={portfolio}
                onChange={(e) => setPortfolio(e.target.value)}
                placeholder="Paste one company per line, for example: NVDA | NVIDIA Corporation"
                style={{ marginTop: 8 }}
              />
              <select className="input" value={portfolioUse} onChange={(e) => setPortfolioUse(e.target.value as typeof portfolioUse)} style={{ marginTop: 8 }}>
                <option value="supplied_only">Evaluate only these companies</option>
                <option value="supplied_and_discovered">Include these and discover additional companies</option>
                <option value="discovered_only">Use as reference and discover outside the portfolio</option>
              </select>
            </div>
          </>
        )}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          {!discovery && <button className="btn primary" onClick={handleStartDiscovery} disabled={loading || question.trim().length < 3}>
            {loading ? 'Starting discovery...' : 'Start guided discovery'}
          </button>}
          {discovery && <button className="btn" onClick={() => { setDiscovery(null); setWorkspaceId(''); setDraft(null); setAnswerValue(''); setRefinementValue('') }}>Start over</button>}
          {workspaceId && <button className="btn" onClick={() => nav(`/workspaces/${workspaceId}`)}>Open workspace</button>}
        </div>

        {!discovery && <div className="workspace-examples">
          {EXAMPLES.map((x) => (
            <button key={x} className="btn" onClick={() => { setQuestion(x); setDraft(null); setDiscovery(null); setWorkspaceId(''); setAnswerValue(''); setRefinementValue('') }}>{x}</button>
          ))}
        </div>}
      </div>

      {error && <ErrorBox error={error} />}
      {discovery && workspaceId && (
        <DiscoveryPanel
          workspaceId={workspaceId}
          discovery={discovery}
          answering={answering}
          planning={planning}
          refining={refining}
          answerValue={answerValue}
          refinementValue={refinementValue}
          setAnswerValue={setAnswerValue}
          setRefinementValue={setRefinementValue}
          onAnswer={handleAnswerDiscovery}
          onPlan={handlePlanDiscovery}
          onRefine={handleRefineProposal}
          onOpen={openAndStartScoping}
          onAuto={buildEverything}
          hasDraft={!!draft}
        />
      )}
      {draft && <DraftPreview draft={draft} workspaceId={workspaceId} onOpen={openAndStartScoping} />}
    </div>
  )
}
